import startsWith from 'lodash/startsWith';
import moment from 'moment';
import _ from 'lodash';
import React, { useState, useEffect } from 'react';
import { FaRegFolderOpen } from 'react-icons/fa';
import { FaRegCircleCheck } from 'react-icons/fa6';
import { IoSearchSharp } from 'react-icons/io5';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import DataTable from '../../../core/components/DataTable';
import { hasRole, RoleTypes } from '../../../core/components/Permission';
import { getApiStatus, toString } from '../../../core/utils';
import { selectRole } from '../../../store/authReducer';
import { getSiteModelYears, getSiteOperationTypes } from '../actions';
import {
  selectSiteGroupId,
  selectManagerSiteId,
  selectSiteModelYears,
  selectSiteOperationTypes,
} from '../reducer';
import * as actions from '../../tasks/actions';
import { buildQuery } from '../../tasks/helpers';
import { Task, TaskType } from '../../tasks/models';
import {
  selectEssentialPage,
  selectEssentialPageSize,
  selectEssentialTasks,
  selectEssentialTotal,
  selectTaskLocations,
  selectTaskPrefixes,
} from '../../tasks/reducer';
import UserGroupPermissions, {
  addPermissionRefs,
} from '../../../core/components/UserGroupPermissions';
import ButtonWithLoader from '../../../core/components/ButtonWithLoader';
import { selectEmployees } from '../../employees/reducer';
import { fetchEmployees } from '../../employees/actions';
import { DrawerContainer, Drawer } from '../../../core/components/Drawer';
import { TaskDetails } from '../../tasks/components/TaskDetails';
import { TASK_DRAWER_WIDTH } from '../../tasks/types';
import { FilterConfig, FilterPlacement, PageFilters } from '../../../core/components/PageFilters';

const styles = {
  tableHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
};

export const EssentialTasks = (): JSX.Element => {
  // subscribes
  const role = useSelector(selectRole),
    essentialTasks = useSelector(selectEssentialTasks),
    total = useSelector(selectEssentialTotal),
    page = useSelector(selectEssentialPage),
    employees = useSelector(selectEmployees),
    pageSize = useSelector(selectEssentialPageSize),
    taskPrefixList = useSelector(selectTaskPrefixes),
    taskLocationList = useSelector(selectTaskLocations),
    siteModelYearList = useSelector(selectSiteModelYears),
    siteOperationTypeList = useSelector(selectSiteOperationTypes),
    groupId = useSelector(selectSiteGroupId),
    siteId = useSelector(selectManagerSiteId),
    tasksFetched = useSelector((state) => getApiStatus(state, 'FETCH_TASKS')),
    savingEssentialTask = useSelector((state) => getApiStatus(state, 'SAVE_ESSENTIAL_TASK')),
    deletingEssentialTask = useSelector((state) => getApiStatus(state, 'DELETE_ESSENTIAL_TASK'));

  // hooks
  const dispatch = useDispatch();
  const intl = useIntl();

  // local state
  const [typeFilter, setTypeFilter] = useState('');
  const [prefixFilter, setPrefixFilter] = useState('');
  const [highlightedRow, setHighlightedRow] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortQuery, setSortQuery] = React.useState({});
  const [isAdding, setIsAdding] = useState(false);
  const [selectedTaskId, setSelectedTaskId] = useState<number | null>(null);

  const getTaskTypeOptions = () => [
    {
      value: toString(TaskType, TaskType.Maintenance),
      text: intl.formatMessage({ id: 'maintenance' }),
    },
    { value: toString(TaskType, TaskType.Project), text: intl.formatMessage({ id: 'project' }) },
    {
      value: toString(TaskType, TaskType.OpenChecklist),
      text: intl.formatMessage({ id: 'openChecklist' }),
    },
    {
      value: toString(TaskType, TaskType.ClosingChecklist),
      text: intl.formatMessage({ id: 'closingChecklist' }),
    },
    {
      value: toString(TaskType, TaskType.PreventativeMaintenance),
      text: intl.formatMessage({ id: 'preventativeMaintenance' }),
    },
    {
      value: toString(TaskType, TaskType.CleaningList),
      text: intl.formatMessage({ id: 'cleaningList' }),
    },
    {
      value: toString(TaskType, TaskType.HourlyInspection),
      text: intl.formatMessage({ id: 'hourlyInspection' }),
    },
    {
      value: toString(TaskType, TaskType.ExpressInterior),
      text: intl.formatMessage({ id: 'expressInterior' }),
    },
  ];

  const getTaskQuery = (queryParameters = {}) => {
    const defaultQuery = { page: 0, siteId: null };
    if (siteId) {
      defaultQuery.siteId = siteId;
    }

    return buildQuery(defaultQuery, queryParameters, {
      typeFilter,
      prefixFilter,
      sortQuery,
    });
  };

  const handleFetchData = (values?: any) => {
    if (!tasksFetched) {
      const taskQuery = getTaskQuery(values);
      dispatch(actions.getEssentialTasks(taskQuery));
    }
  };

  const fetchSortedTasks = (sortParameters: any) => {
    setSortQuery(sortParameters);
  };

  useEffect(() => {
    handleFetchData();
    if (!taskPrefixList.length) {
      dispatch(actions.getTaskPrefixes());
    }
    if (!taskLocationList.length) {
      dispatch(actions.getTaskLocations());
    }
    if (!employees.length) {
      dispatch(fetchEmployees());
    }
    if (hasRole(role, RoleTypes.OperatorGroupAdmin)) {
      if (!siteModelYearList.length) {
        dispatch(getSiteModelYears());
      }

      if (!siteOperationTypeList.length) {
        dispatch(getSiteOperationTypes());
      }
    }
  }, []);

  useEffect(() => {
    handleFetchData();
  }, [typeFilter, prefixFilter, groupId, sortQuery, savingEssentialTask]);

  useEffect(() => {
    handleFetchData();
  }, [deletingEssentialTask]);

  const getPaginationInfo = () => {
    return {
      total: total,
      page,
      pageSize: pageSize,
      goToPage: (value: number) => handleFetchData({ page: value }),
      location,
      status,
    };
  };

  const addTaskWithGroupId = (task: Task) => {
    dispatch(actions.addEssentialTask(task));
  };

  const addDialogProps = {
    title: intl.formatMessage({ id: 'addEssentialTask' }),
    onSubmit: (value: any) => addTaskWithGroupId(value),
    isNew: true,
    isEssentialTask: true,
  };

  const rowClickFunction = (data: any) => {
    setHighlightedRow(data._id);
    setSelectedTaskId(data._id);
  };

  const handleEssentialTaskAdd = () => {
    setIsAdding(true);
  };

  const handleCloseDrawer = () => {
    setSelectedTaskId(null);
    setIsAdding(false);
  };

  const getRowClickInfo = () => ({
    callback: (data: any) => {
      rowClickFunction(data);
    },
  });

  const initializeDatatable = (filteredData: any) => {
    const headers: any = [
      { name: intl.formatMessage({ id: 'name' }), databaseProperty: 'name' },
      { name: intl.formatMessage({ id: 'interval' }), databaseProperty: 'interval' },
      { name: intl.formatMessage({ id: 'startDate' }), databaseProperty: 'start' },
      { name: intl.formatMessage({ id: 'endDate' }), databaseProperty: 'end' },
    ];
    headers.push({
      name: intl.formatMessage({ id: 'resource' }),
      cell: {
        component: (componentProps: any) => {
          const url = startsWith(componentProps.rowData._resource, 'http')
            ? componentProps.rowData._resource
            : `https://${componentProps.rowData._resource}`;
          return (
            <a href={url} target="_blank" rel="noopener noreferrer">
              {componentProps.rowData._resource && (
                <i
                  className="icon icon-link px-4 text-info"
                  title={`${componentProps.rowData._resource}`}
                />
              )}
            </a>
          );
        },
      },
    });

    const data = filteredData.map((task: Task) => ({
      _id: task.id,
      name: task.name,
      intervalDescription: task ? task.getIntervalDescription(intl.formatMessage) : '',
      start: moment(task.start).format('MM/DD/YYYY'),
      end: task.end && moment(task.end).format('MM/DD/YYYY'),
      _resource: task.resource,
    }));

    return {
      headers,
      data,
    };
  };

  const normalizedSearchTerm = searchTerm.trim().toLowerCase();
  const data = normalizedSearchTerm
    ? essentialTasks.filter((task: Task) => task.name?.toLowerCase().includes(normalizedSearchTerm))
    : essentialTasks;

  const filterConfigs: FilterConfig[] = [
    {
      id: 'searchTerm',
      type: 'search',
      name: intl.formatMessage({ id: 'search', defaultMessage: 'Search' }),
      placement: FilterPlacement.Chiclet,
      defaultVisible: true,
      icon: <IoSearchSharp />,
      searchTerm,
      debounceMs: 0,
      onChange: (value: string | null) => setSearchTerm(value || ''),
    },
    {
      id: 'typeFilter',
      type: 'select',
      name: intl.formatMessage({ id: 'taskType' }),
      placement: FilterPlacement.Chiclet,
      defaultVisible: true,
      multiSelect: false,
      icon: <FaRegCircleCheck />,
      options: getTaskTypeOptions().map((option) => ({
        ...option,
        selected: String(typeFilter) === String(option.value),
      })),
      onChange: (values: string[]) => {
        const nextTypeFilter = values[0] || '';
        setTypeFilter(nextTypeFilter);
        if (nextTypeFilter !== toString(TaskType, TaskType.Project)) {
          setPrefixFilter('');
        }
      },
    },
  ];

  if (typeFilter === toString(TaskType, TaskType.Project)) {
    filterConfigs.push({
      id: 'prefixFilter',
      type: 'select',
      name: intl.formatMessage({ id: 'taskPrefix' }),
      placement: FilterPlacement.Chiclet,
      defaultVisible: true,
      multiSelect: false,
      icon: <FaRegFolderOpen />,
      options: (taskPrefixList || []).map((prefix: any) => ({
        value: String(prefix.type),
        text: prefix.message,
        selected: String(prefixFilter) === String(prefix.type),
      })),
      onChange: (values: string[]) => setPrefixFilter(values[0] || ''),
    });
  }

  return (
    <div data-testid="essentialTaskContainer" className="maintenance-tasks-container">
      <div className="task-table-header tasks-buttons-container">
        <div style={styles.tableHeader} className="filters-actions-container">
          <PageFilters filters={filterConfigs} maxChicletFilters={4} />
        </div>
        <UserGroupPermissions itemRef={addPermissionRefs.task}>
          <ButtonWithLoader style="button add-task" handleClick={() => handleEssentialTaskAdd()}>
            <>
              <span data-testid="addEssentialTaskButton" className="add-button-text">
                {intl.formatMessage({ id: 'addEssentialTask' })}
              </span>
            </>
          </ButtonWithLoader>
        </UserGroupPermissions>
      </div>
      <DrawerContainer>
        <div data-testid="EssentialTasksearchBar">
          <DataTable
            paginationInfo={getPaginationInfo()}
            rowClickInfo={getRowClickInfo()}
            highlightedRow={highlightedRow}
            scrolling
            fetchSortedData={fetchSortedTasks}
            {...initializeDatatable(data)}
          />
        </div>
        <Drawer
          expanded={!!selectedTaskId || isAdding}
          maximized={false}
          expandedWidth={TASK_DRAWER_WIDTH}
          onBackdropClick={handleCloseDrawer}
          overlay
        >
          {(selectedTaskId || isAdding) && (
            <TaskDetails
              taskId={selectedTaskId}
              isEssential={true}
              isNew={isAdding}
              onClose={handleCloseDrawer}
              onSave={handleFetchData}
              onDelete={handleFetchData}
            />
          )}
        </Drawer>
      </DrawerContainer>
    </div>
  );
};

export default EssentialTasks;
